import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { themeClasses, Button } from '@member-portal/ui';
import { useAuth } from '@/contexts/AuthContext';

type LoginStep = 'email' | 'otp';

function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/fhxd-auth/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setStep('otp');
        startResendCountdown();
      } else {
        setError(data.message || 'Failed to send access code. Please try again.');
      }
    } catch (err) {
      console.error('Request OTP error:', err);
      setError('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/fhxd-auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code: otpCode }),
      });

      const data = await response.json();

      if (data.success) {
        // Store auth token, optional Salesforce token, and user info via context
        const salesforceToken = data.data.salesforceToken ?? 'fhxd-lead-auth';
        login(data.data.token, data.data.user, salesforceToken);

        // Redirect to hotels page (or dashboard in the future)
        navigate('/collection');
      } else {
        setError(data.message || 'Invalid access code. Please try again.');
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const startResendCountdown = () => {
    setResendDisabled(true);
    setResendCountdown(60);

    const interval = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/fhxd-auth/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        startResendCountdown();
      } else {
        setError(data.message || 'Failed to resend access code. Please try again.');
      }
    } catch (err) {
      console.error('Resend OTP error:', err);
      setError('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setOtpCode('');
    setError(null);
  };

  return (
    <div className="fhxd-chrome-top">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/images/burj-2.jpg)' }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-title font-normal text-3xl md:text-5xl lg:text-6xl tracking-wider uppercase text-white mb-6">
            Advisor Login
          </h1>
          <p className="font-body font-normal leading-relaxed tracking-form text-white/90 max-w-2xl mx-auto">
            Access your Four Hundred by Design advisor portal
          </p>
        </div>
      </section>

      {/* Login Form Section */}
      <section className="py-16 bg-theme-bg">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          {step === 'email' ? (
            <>
              <h2 className={`${themeClasses.title.h3} text-center mb-2`}>
                Sign In
              </h2>
              <p className={`${themeClasses.text.mutedSmall} text-center mb-8`}>
                We'll send you an access code
              </p>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm font-normal">
                  {error}
                </div>
              )}

              <form onSubmit={handleEmailSubmit} className="space-y-8">
                <div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError(null);
                    }}
                    placeholder="Email address"
                    className={`${themeClasses.form.input} font-normal`}
                    autoComplete="email"
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || !email}
                  className="w-full"
                >
                  {loading ? 'Sending...' : 'Continue'}
                </Button>
              </form>

              <div className="mt-8 text-center">
                <p className={themeClasses.text.mutedSmall}>
                  Don't have an account?{' '}
                  <Link
                    to="/signup"
                    className="underline hover:text-theme-text transition-colors"
                  >
                    Register
                  </Link>
                </p>
              </div>
            </>
          ) : (
            <>
              <h2 className={`${themeClasses.title.h3} text-center mb-2`}>
                Enter Access Code
              </h2>
              <p className={`${themeClasses.text.mutedSmall} text-center mb-8`}>
                Sent to {email}
              </p>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm font-normal">
                  {error}
                </div>
              )}

              <form onSubmit={handleOtpSubmit} className="space-y-8">
                <div>
                  <input
                    type="text"
                    id="otpCode"
                    name="otpCode"
                    required
                    value={otpCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setOtpCode(value);
                      if (error) setError(null);
                    }}
                    placeholder="000000"
                    className={`${themeClasses.form.input} font-normal text-center text-3xl tracking-[0.5em] py-4`}
                    maxLength={6}
                    autoComplete="one-time-code"
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || otpCode.length !== 6}
                  className="w-full"
                >
                  {loading ? 'Verifying...' : 'Sign In'}
                </Button>
              </form>

              <div className="mt-8 text-center space-y-4">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendDisabled || loading}
                  className={`${themeClasses.text.mutedSmall} underline transition-colors ${
                    resendDisabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:text-theme-text'
                  }`}
                >
                  {resendDisabled
                    ? `Resend code in ${resendCountdown}s`
                    : 'Resend code'}
                </button>
                <span className={`${themeClasses.text.mutedSmall} mx-3`}>·</span>
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className={`${themeClasses.text.mutedSmall} underline hover:text-theme-text transition-colors`}
                >
                  Change email
                </button>
              </div>
            </>
          )}

          {/* Support Info */}
          <div className="mt-12 pt-8 border-t border-theme-border text-center">
            <p className={`${themeClasses.text.mutedSmall}`}>
              Need help?{' '}
              <a
                href="mailto:info@fourhundredbydesign.com"
                className="underline hover:text-theme-text transition-colors"
              >
                Contact support
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LoginPage;
